import { Check, X } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { SUBJECTS } from '@jeraisonne/db';
import { AvatarCircle } from '@/components/avatar-circle';
import {
  usePendingTutorInvitations,
  useRespondTutorInvitation,
  type PendingTutorInvitation,
} from '@/lib/student/tutor-invitations';

interface Props {
  studentId: string;
}

export function TutorInvitationBanner({ studentId }: Props) {
  const { data: invitations } = usePendingTutorInvitations(studentId);

  if (!invitations?.length) return null;

  return (
    <div className="space-y-3">
      {invitations.map((inv) => (
        <InvitationCard key={inv.link_id} invitation={inv} studentId={studentId} />
      ))}
    </div>
  );
}

function InvitationCard({
  invitation,
  studentId,
}: {
  invitation: PendingTutorInvitation;
  studentId: string;
}) {
  const respond = useRespondTutorInvitation(studentId);
  const isPending = respond.isPending;

  return (
    <div className="border-primary-200 bg-primary-50 flex items-center gap-3 rounded-2xl border px-4 py-3">
      <AvatarCircle
        name={invitation.tutor_name}
        src={invitation.tutor_avatar_url}
        size="md"
        className="shrink-0"
      />

      <div className="min-w-0 flex-1">
        <p className="font-display text-ink-900 truncate text-sm font-bold">
          {invitation.tutor_name}
        </p>
        <p className="text-ink-500 mt-0.5 font-sans text-xs">
          {invitation.subjects.length === 0
            ? "t'a invité à rejoindre son suivi"
            : `t'invite pour ${invitation.subjects
                .map((s) => SUBJECTS.find((sub) => sub.value === s)?.label ?? s)
                .join(', ')}`}
        </p>
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <button
          onClick={() => respond.mutate({ linkId: invitation.link_id, accept: false })}
          disabled={isPending}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
            'text-ink-400 hover:text-error-500 hover:bg-error-50',
          )}
          title="Refuser"
        >
          <X className="h-4 w-4" />
        </button>
        <button
          onClick={() => respond.mutate({ linkId: invitation.link_id, accept: true })}
          disabled={isPending}
          className={cn(
            'font-display flex h-8 items-center gap-1.5 rounded-lg px-3 text-xs font-bold transition-colors active:scale-[0.98]',
            'bg-primary-500 hover:bg-primary-600 text-white',
          )}
        >
          <Check className="h-3.5 w-3.5" />
          Accepter
        </button>
      </div>
    </div>
  );
}
